import React, { useCallback, useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Form, Spinner } from 'react-bootstrap';
import { Search } from 'lucide-react';
import { api } from '../../utils/apiClient';
import { showToast } from '../../utils/toast';
import { formatMoney } from '../../components/quote/TotalsPanel';
import { ROUTE } from '../../utils/navigation';
import './business.css';

const CustomersPage = ({ onNavigate, storeCurrency = 'USD' }) => {
  const { t } = useTranslation();
  const [loading, setLoading] = useState(true);
  const [syncing, setSyncing] = useState(false);
  const [filter, setFilter] = useState('');
  const [customers, setCustomers] = useState([]);
  const [syncOn, setSyncOn] = useState(true);

  const load = useCallback(async (q = '') => {
    setLoading(true);
    try {
      const data = await api.listCustomers(q);
      setCustomers(data?.customers || []);
    } catch (err) {
      showToast(err.message || t('business.customers.loadFailed'), 'error');
      setCustomers([]);
    } finally {
      setLoading(false);
    }
  }, [t]);

  useEffect(() => {
    const timer = setTimeout(() => load(filter), filter ? 250 : 0);
    return () => clearTimeout(timer);
  }, [filter, load]);

  const onSyncToggle = async (checked) => {
    setSyncOn(checked);
    if (!checked) return;
    setSyncing(true);
    try {
      const res = await api.syncCustomers();
      showToast(
        t('business.customers.synced', {
          customers: res.customers || 0,
          companies: res.companies || 0,
        }),
        'success',
      );
      await load(filter);
    } catch (err) {
      showToast(err.message || t('business.customers.syncFailed'), 'error');
      setSyncOn(false);
    } finally {
      setSyncing(false);
    }
  };

  return (
    <div className="q2c-biz">
      <div className="q2c-biz-header">
        <div>
          <h2>{t('business.customers.title')}</h2>
          <p>{t('business.customers.subtitle')}</p>
        </div>
      </div>

      <div className="q2c-biz-toolbar">
        <div className="q2c-biz-search">
          <Search size={16} />
          <Form.Control
            value={filter}
            onChange={(e) => setFilter(e.target.value)}
            placeholder={t('business.customers.filterPlaceholder')}
          />
        </div>
        <Form.Check
          type="switch"
          id="sync-customers"
          label={syncing ? t('business.customers.syncing') : t('business.customers.syncCustomers')}
          checked={syncOn}
          disabled={syncing}
          onChange={(e) => onSyncToggle(e.target.checked)}
        />
      </div>

      <div className="q2c-biz-panel">
        {loading ? (
          <div className="text-center py-4">
            <Spinner animation="border" size="sm" />
          </div>
        ) : customers.length === 0 ? (
          <div className="text-muted text-center py-4">
            {t('business.customers.empty')}
          </div>
        ) : (
          <div className="q2c-biz-table-wrap">
            <table className="q2c-biz-table">
              <thead>
                <tr>
                  <th>{t('business.customers.colName')}</th>
                  <th>{t('business.customers.colEmail')}</th>
                  <th>{t('business.customers.colCompany')}</th>
                  <th>{t('business.customers.colQuotes')}</th>
                  <th>{t('business.customers.colTotalValue')}</th>
                </tr>
              </thead>
              <tbody>
                {customers.map((c) => (
                  <tr key={c.id}>
                    <td>
                      <button
                        type="button"
                        className="q2c-biz-link"
                        onClick={() => onNavigate?.(`${ROUTE.CUSTOMERS}/${c.id}`)}
                      >
                        {c.name}
                      </button>
                    </td>
                    <td>{c.email}</td>
                    <td>{c.companyName || '—'}</td>
                    <td>{c.quoteCount}</td>
                    <td>{formatMoney(c.totalValue, c.currency || storeCurrency)}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>
    </div>
  );
};

export default CustomersPage;
